import { useState } from "react";
import { Box, CircularProgress, Grid, SxProps, Typography } from "@mui/material";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { CustomButton, InfoCard } from "src/design";
import { Payment } from "src/business/payment/payment";
import { downloadProof } from "src/data/payment/queries/payment";
import {
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
  colorWarning,
} from "src/design/tokens";
import { useProofDownload } from "../hooks/useProofDownload";
import { PaymentStatusChip } from "./PaymentStatusChip";
import { ReplaceProofDialog } from "./ReplaceProofDialog";
import { CancelPaymentDialog } from "./CancelPaymentDialog";

interface PendingPaymentCardProps {
  payment: Payment;
  restaurantId: string;
  /** Raised on a 409 from any action — the payment is no longer pending. */
  onConflict: () => void;
  sx?: SxProps;
}

const Detail = ({ label, value }: { label: string; value: string }) => (
  <Grid item xs={6} md={3}>
    <Typography variant="caption" sx={{ color: colorTextMuted }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
      {value}
    </Typography>
  </Grid>
);

/**
 * The payment awaiting review, with the three things its declarer can still do
 * to it. Everything here disappears the moment an admin resolves it — which is
 * why every action reports a 409 back to the page rather than swallowing it.
 */
export const PendingPaymentCard = ({
  payment,
  restaurantId,
  onConflict,
  sx,
}: PendingPaymentCardProps) => {
  const { t, i18n } = useTranslation();
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const { download, downloadingId, errorKey } = useProofDownload();

  const isDownloading = downloadingId === payment.id;

  return (
    <>
      <InfoCard
        title={t("payments.pending.title")}
        subtitle={t("payments.pending.subtitle")}
        icon={<HourglassTopOutlinedIcon sx={{ color: colorWarning }} />}
        action={<PaymentStatusChip status={payment.status} />}
        sx={sx}
      >
        <Grid container spacing={2} sx={{ py: 1 }}>
          <Detail
            label={t("payments.history.columns.type")}
            value={t(`payments.type.${payment.type}`)}
          />
          <Detail
            label={t("payments.history.columns.tier")}
            value={t(tierTitleKey(payment.targetTier), {
              defaultValue: payment.targetTier,
            })}
          />
          <Detail
            label={t("payments.history.columns.duration")}
            value={t("payments.declare.periodDuration", {
              period: t(`payments.period.${payment.period}`),
              duration: t("payments.months", { count: payment.months }),
            })}
          />
          <Detail
            label={t("payments.history.columns.amount")}
            value={t("payments.amount", {
              amount: formatPrice(payment.amount, i18n.language),
            })}
          />

          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: colorTextMuted }}>
              {t("payments.pending.declaredOn", {
                date: dayjs(payment.createdAt).format("MMMM D, YYYY"),
              })}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <CustomButton
                color="inverted"
                disabled={isDownloading}
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DownloadOutlinedIcon fontSize="small" />
                  )
                }
                onClick={() =>
                  download(payment.id, () =>
                    downloadProof(restaurantId, payment.id),
                  )
                }
              >
                {t("payments.pending.downloadProof")}
              </CustomButton>

              <CustomButton
                color="inverted"
                startIcon={<SwapHorizOutlinedIcon fontSize="small" />}
                onClick={() => setReplaceOpen(true)}
              >
                {t("payments.pending.replaceProof")}
              </CustomButton>

              <CustomButton
                color="inverted"
                startIcon={<CloseOutlinedIcon fontSize="small" />}
                onClick={() => setCancelOpen(true)}
                sx={{ color: colorTextError, borderColor: colorTextError }}
              >
                {t("payments.pending.cancel")}
              </CustomButton>
            </Box>

            {errorKey && (
              <Typography variant="caption" sx={{ color: colorTextError }}>
                {t(errorKey)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </InfoCard>

      <ReplaceProofDialog
        open={replaceOpen}
        onClose={() => setReplaceOpen(false)}
        restaurantId={restaurantId}
        paymentId={payment.id}
        onConflict={onConflict}
      />

      <CancelPaymentDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        restaurantId={restaurantId}
        paymentId={payment.id}
        onConflict={onConflict}
      />
    </>
  );
};
